import exportFromJSON from "export-from-json";
import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { DropdownItem } from "@dashboard/devices/components/shared";
import { EntityManagerRow } from "@dashboard/shared/components/EntityManagerRow/EntityManagerRow";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { skipToken } from "@reduxjs/toolkit/query";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";

import { AssignedGroupsSection } from "./components/AssignedGroupsSection/AssignedGroupsSection";
import { CommandsSection } from "./components/CommandsSection/CommandsSection";
import CreatePolicyModal from "./components/CreatePolicyModal/CreatePolicyModal";
import DeletePolicyModal from "./components/DeletePolicyModal/DeletePolicyModal";
import EditPolicyModal from "./components/EditPolicyModal/EditPolicyModal";
import { ImportPolicyDialog } from "./components/ImportPolicyDialog/ImportPolicyDialog";
import { usePolicyLimit } from "./hooks/usePolicyLimit/usePolicyLimit";
import { useGetPoliciesQuery } from "./api";
import { useStyles } from "./PoliciesPage.style";
import { useCurrentPolicy } from "./PoliciesPage.utils";

export const PoliciesManager = (props: { policies: API.PolicyViewModel[]; loadFailed?: boolean }) => {
    const [isDeletePolicyModalOpen, setIsDeletePolicyModalOpen] = React.useState(false);
    const [isCreatePolicyModalOpen, setIsCreatePolicyModalOpen] = React.useState(false);
    const [isEditPolicyModalOpen, setIsEditPolicyModalOpen] = React.useState(false);
    const [isImportPolicyModalOpen, setIsImportPolicyModalOpen] = React.useState(false);
    const [currentPolicyId, setCurrentPolicyId] = useCurrentPolicy(props.policies);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const { data: workspace = null } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();

    const { max: policyLimit, isPolicyLimitReached } = usePolicyLimit();

    const currentPolicy = props.policies.find((item) => item.id === currentPolicyId);
    const isCreationProcess = !currentPolicy?.id;

    const cannotCreatePolicy = workspaceAbility(workspace).cannot("create", "Policy");
    const cannotUpdatePolicy = workspaceAbility(workspace).cannot("update", "Policy");
    const cannotDeletePolicy = workspaceAbility(workspace).cannot("delete", "Policy");

    const cannotAddPolicy = !!props.loadFailed || cannotCreatePolicy || isPolicyLimitReached;

    const policyOptions = props.policies.map((policy) => ({
        label: policy.name,
        value: policy.id,
    }));

    const getAddPolicyTooltip = () => {
        if (props.loadFailed) {
            return t("pages:policiesPage.tooltip.policiesUnavailable");
        }
        if (cannotCreatePolicy) {
            return t("common:tooltip.cannotManagePolicies");
        }
        if (isPolicyLimitReached) {
            return t("pages:policiesPage.tooltip.policyLimitReached", { max: policyLimit });
        }
        return "";
    };

    const handlePolicyChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        setCurrentPolicyId(selectedOption.value);
    };

    const onPolicyCreated = (policyId: string) => {
        setCurrentPolicyId(policyId);
    };

    const onPolicyDeleted = () => {
        const remainingPolicies = props.policies.filter((item) => item.id !== currentPolicyId);
        setCurrentPolicyId(remainingPolicies[0]?.id ?? null);
    };

    const exportPolicy = () => {
        if (!currentPolicy) {
            return;
        }
        exportFromJSON({
            data: { name: currentPolicy.name, content: currentPolicy.content ?? [] },
            fileName: currentPolicy.name,
            exportType: exportFromJSON.types.json,
        });
    };

    return (
        <React.Fragment>
            <PageContainer>
                <ExpiredWrapper>
                    {props.loadFailed ? (
                        <CustomAlert severity="error" className={classes.alertContainer}>
                            {t("common:error.policies.failedToGetPolicies")}
                        </CustomAlert>
                    ) : null}

                    {isCreationProcess && !props.loadFailed ? (
                        <CustomAlert severity="info" className={classes.alertContainer}>
                            {t("pages:policiesPage.hint.noPolicySelected")}
                        </CustomAlert>
                    ) : null}

                    <EntityManagerRow
                        selector={
                            <SelectField
                                label={
                                    isCreationProcess
                                        ? t("pages:policiesPage.input.createNewPolicy.caption")
                                        : t("common:label.policy")
                                }
                                value={policyOptions.find((option) => option.value === currentPolicyId)}
                                onChange={handlePolicyChange}
                                options={policyOptions}
                                disabled={!props.policies.length}
                                fullWidth
                                searchable
                            />
                        }
                    >
                        <DropdownItem
                            label={t("common:button.create")}
                            iconName="fa-solid fa-fw fa-plus"
                            onClick={() => setIsCreatePolicyModalOpen(true)}
                            disabled={cannotAddPolicy}
                            tooltipProps={{
                                title: getAddPolicyTooltip(),
                                hide: !cannotAddPolicy,
                            }}
                        />
                        <DropdownItem
                            label={t("common:button.rename")}
                            iconName="fa-solid fa-fw fa-pencil"
                            onClick={() => setIsEditPolicyModalOpen(true)}
                            disabled={isCreationProcess || cannotUpdatePolicy}
                            tooltipProps={{
                                title: isCreationProcess
                                    ? t("pages:policiesPage.tooltip.noPolicySelected")
                                    : cannotUpdatePolicy
                                      ? t("common:tooltip.cannotManagePolicies")
                                      : t("common:tooltip.editName"),
                            }}
                        />
                        <DropdownItem
                            label={t("common:button.import")}
                            iconName="fa-solid fa-fw fa-file-import"
                            onClick={() => setIsImportPolicyModalOpen(true)}
                            disabled={cannotAddPolicy}
                            tooltipProps={{
                                title: getAddPolicyTooltip(),
                                hide: !cannotAddPolicy,
                            }}
                        />
                        <DropdownItem
                            label={t("common:button.export")}
                            iconName="fa-solid fa-fw fa-file-export"
                            onClick={exportPolicy}
                            disabled={isCreationProcess}
                            tooltipProps={{
                                title: t("pages:policiesPage.tooltip.noPolicySelected"),
                                hide: !isCreationProcess,
                            }}
                        />
                        <DropdownItem
                            label={t("common:button.delete")}
                            iconName="fa-solid fa-fw fa-trash"
                            danger
                            onClick={() => setIsDeletePolicyModalOpen(true)}
                            disabled={isCreationProcess || cannotDeletePolicy}
                            tooltipProps={{
                                title: cannotDeletePolicy
                                    ? t("common:tooltip.cannotManagePolicies")
                                    : t("pages:policiesPage.tooltip.noPolicySelected"),
                                hide: !isCreationProcess && !cannotDeletePolicy,
                            }}
                        />
                    </EntityManagerRow>

                    <div className={classes.sectionsContainer}>
                        <AssignedGroupsSection policy={currentPolicy ?? null} />
                        <CommandsSection policy={currentPolicy ?? null} readOnly={cannotUpdatePolicy} />
                    </div>
                </ExpiredWrapper>
            </PageContainer>
            {currentPolicy ? (
                <React.Fragment>
                    <DeletePolicyModal
                        policy={currentPolicy}
                        open={isDeletePolicyModalOpen}
                        onClose={() => setIsDeletePolicyModalOpen(false)}
                        onDeleted={onPolicyDeleted}
                    />
                    <EditPolicyModal
                        policy={currentPolicy}
                        policies={props.policies}
                        open={isEditPolicyModalOpen}
                        onClose={() => setIsEditPolicyModalOpen(false)}
                    />
                </React.Fragment>
            ) : null}
            <ImportPolicyDialog
                open={isImportPolicyModalOpen}
                onClose={() => setIsImportPolicyModalOpen(false)}
                onCreated={onPolicyCreated}
                policies={props.policies}
            />
            <CreatePolicyModal
                open={isCreatePolicyModalOpen}
                onClose={() => setIsCreatePolicyModalOpen(false)}
                onCreated={onPolicyCreated}
                policies={props.policies}
            />
        </React.Fragment>
    );
};

const PoliciesPage = () => {
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const {
        data: policiesResponse,
        isLoading,
        isError,
    } = useGetPoliciesQuery(workspaceId !== null && !isSubscriptionExpired ? { workspaceId } : skipToken);
    const policies = policiesResponse?.list ?? [];

    const sortedPolicies = _.sortBy([...policies], (item) => item.name?.toLowerCase());

    if (isLoading) {
        return <PageContainer isLoading />;
    }

    return <PoliciesManager policies={sortedPolicies} loadFailed={isError} />;
};

export default withMetaDescription(PoliciesPage, {
    title: i18next.t("pages:policiesPage.metaTitle"),
});
